import useAxios from "../../utils/useAxios";
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { Dropdown } from "antd";
import { Edit, MoreVert } from "@mui/icons-material";
import { DocsModal } from "../Documents/DocumentModal";
import { useContentType } from "../../utils/contentTypes";
import { useAccessControl } from "../../hooks/useAccessControl";
import { buildContextMenu } from "../Menu/DynamicMenu";
import AssetCardProgressTracker from "../../features/Logistics/components/progress/AssetCardProgressTracker";
import { isTrackedLogisticsAsset } from "../../features/Logistics/models/logisticsProgressPayload";
import useLongPress from "../../features/Logistics/hooks/useLongPress";
import LogisticsNextAction from "../../features/Logistics/components/progress/LogisticsNextAction";
import { useLogisticsUpdateLauncher } from "../../features/Logistics/context/logisticsUpdateLauncherContext";
import { FAULT_COLORS } from "../../utils/faults";
import {
  COARSE_POINTER,
  INSTRUMENT_LIST_ACTIONS_WIDTH,
  INSTRUMENT_LIST_ACTIONS_WIDTH_TOUCH,
} from "./instrumentListLayout";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const InstrumentCard = ({
  instrument,
  instrumentId,
  handleDeleteClick,
  handleInfoClick,
  handleEditInstrument,
  selectedInstrument,
  setSelectedInstrument,
  permissionsLoading,
  viewMode,
  logisticsVariant = "default",
  showLogisticsDetails = false,
  logisticsActionsSlot = null,
  onLogisticsSelect,
  onLogisticsLongPress = null,
  logisticsIsChecked = false,
}) => {
  const api = useAxios();
  const navigate = useNavigate();
  const { siteId, plantId, assetId, subAssetId } = useParams();
  const { wasLongPress, ...longPressHandlers } = useLongPress(onLogisticsLongPress);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [permissionsRequested, setPermissionsRequested] = useState(false);
  const { contentTypeId, loading: contentTypeLoading } = useContentType(
    api,
    "instrument",
  );

  const instrumentSeverity = instrument.fault?.severity || "healthy";
  const ribbonColor = FAULT_COLORS[instrumentSeverity];
  const shouldShowLogisticsDetails =
    logisticsVariant === "logistics" && showLogisticsDetails;
  const wrappedLogisticsActionsSlot = logisticsActionsSlot ? (
    <Box
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      sx={{
        width: 32,
        height: 24,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        mt: "1px",
      }}
    >
      {logisticsActionsSlot}
    </Box>
  ) : null;
  const shouldUseLogisticsTrackerLayout =
    logisticsVariant === "logistics" && Boolean(logisticsActionsSlot);
  const shouldLoadPermissions = permissionsRequested || dropdownOpen;

  const { canEdit, canDelete, canShare } = useAccessControl(
    "instrument",
    instrument.id,
    { enabled: shouldLoadPermissions },
  );

  const requestPermissions = () => setPermissionsRequested(true);

  const handleDropdownOpenChange = (nextOpen) => {
    if (nextOpen) requestPermissions();
    setDropdownOpen(nextOpen);
  };

  const handleCardClick = () => {
    setSelectedInstrument(instrument);
  };

  const handlePrimaryCardClick = () => {
    if (wasLongPress()) return;

    if (onLogisticsSelect) {
      onLogisticsSelect(instrument);
      return;
    }

    handleCardClick();
  };

  const logisticsUpdate = useLogisticsUpdateLauncher();

  const menuItems = buildContextMenu({
    type: "instrument",
    object: instrument,
    objectId: instrument.id,
    name: instrument.tagNumber,
    canEdit,
    canDelete,
    canShare,
    contentTypeId,
    contentTypeLoading,
    onInfo: handleInfoClick,
    onUpdateLogistics: logisticsUpdate.canUpdate(instrument) ? logisticsUpdate.open : undefined,
    onEdit: handleEditInstrument,
    onDelete: handleDeleteClick,
    DocsComponent: <DocsModal objectType="instrument" object={instrument} />,
  });

  const sharedCardSx = {
    border:
      selectedInstrument?.id === instrumentId
        ? "2px solid #1976d2"
        : "1px solid #e0e0e0",
    backgroundColor:
      selectedInstrument?.id === instrumentId ? "#e3f2fd" : "background.paper",
    boxShadow:
      selectedInstrument?.id === instrumentId
        ? "0 6px 20px rgba(25, 118, 210, 0.25)"
        : "0 3px 6px rgba(0,0,0,0.1)",
    // Extra feedback for a long-press-selected card on touch, alongside the
    // checkbox (which is visible there too, not hidden).
    ...(logisticsIsChecked && {
      "@media (pointer: coarse)": {
        border: "2px solid #1976d2",
        backgroundColor: "#e3f2fd",
        boxShadow: "0 6px 20px rgba(25, 118, 210, 0.25)",
      },
    }),
    // Without this, holding a finger on the card for ~500ms lets the browser
    // start its own native long-press gesture first (text selection on
    // Android, the callout menu on iOS) — that fires pointercancel and
    // clears useLongPress's timer before it reaches threshold, so our
    // custom long-press never fires on a real touch device even though it
    // works in tests. touchAction stays "manipulation" (not "none") so
    // vertical list scrolling that starts on a card still works.
    ...(onLogisticsLongPress && {
      userSelect: "none",
      WebkitUserSelect: "none",
      WebkitTouchCallout: "none",
      touchAction: "manipulation",
    }),
  };

  // Read nested serializer fields produced by InstrumentsSerializer
  // e.g. instrument_type_details.name, communication_protocol_details.name
  const instrumentTypeName = instrument.instrument_type_details?.name ?? null;
  // panel_details comes from ioPanel FK → TierSummarySerializer
  const ioPanelName =
    instrument.panel_details?.tagNumber ||
    instrument.panel_details?.name ||
    instrument.panel_details?.description ||
    null;

  // pid_details comes from pid FK → EquipmentSummarySerializer
  const pidTagNumber = instrument.pid_details?.tagNumber ?? null;
  const commProtocolName =
    instrument.communication_protocol_details?.name ?? null;
  // panel_component_details comes from browsing the hierarchy (nested
  // serializer field); componentTagNumber/componentName come from search
  // results instead, which don't include nested detail objects — fall back
  // to whichever is available. This is the instrument's structural parent.
  const parentComponentTag =
    instrument.panel_component_details?.tagNumber ||
    instrument.componentTagNumber ||
    instrument.componentName ||
    null;

  const FieldRow = ({ label, value }) =>
    value ? (
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            textAlign: "right",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "60%",
          }}
        >
          {value}
        </Typography>
      </Box>
    ) : null;

  if (viewMode === "grid") {
    return (
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Card
          onClick={handlePrimaryCardClick}
          onContextMenu={(event) => {
            if (onLogisticsLongPress) event.preventDefault();
          }}
          {...longPressHandlers}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            borderRadius: "20px",
            position: "relative",
            cursor: "pointer",
            transition: "all 0.25s ease",
            bgcolor: "background.paper",
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            "&:hover": {
              boxShadow: "0 12px 40px rgba(0,0,0,0.14)",
              transform: "translateY(-4px)",
              borderColor: "primary.main",
            },
            ...sharedCardSx,
          }}
        >
          {/* Top accent bar */}
          <Box sx={{ height: 6, bgcolor: ribbonColor, width: "100%" }} />
          <Box
            sx={
              shouldUseLogisticsTrackerLayout
                ? {
                    display: "grid",
                    gridTemplateColumns: "32px minmax(0, 1fr) 32px",
                    alignItems: "flex-start",
                    columnGap: 1,
                    pt: 1.25,
                    px: 1.5,
                    pb: 0.5,
                  }
                : {
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    pt: 1.25,
                    // The real tracker's rightmost node needs pr:7 clearance
                    // from the floating ⋮ menu; an untracked item's invisible
                    // tracker doesn't, so it stays centered on the card via
                    // symmetric padding.
                    pr: isTrackedLogisticsAsset(instrument) ? 7 : 2,
                    pb: 0.5,
                    pl: 2,
                  }
            }
          >
            {shouldUseLogisticsTrackerLayout ? (
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                {wrappedLogisticsActionsSlot}
              </Box>
            ) : (
              wrappedLogisticsActionsSlot
            )}
            <Box
              sx={
                shouldUseLogisticsTrackerLayout
                  ? { minWidth: 0 }
                  : { flex: 1, minWidth: 0 }
              }
            >
              <AssetCardProgressTracker item={instrument} />
            </Box>
            {shouldUseLogisticsTrackerLayout && <Box aria-hidden="true" />}
          </Box>

          {/* Card Header */}
          <CardHeader
            action={
              <Dropdown
                menu={{ items: menuItems }}
                trigger={["click"]}
                open={dropdownOpen}
                onOpenChange={handleDropdownOpenChange}
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton
                  aria-label="More options"
                  onClick={(e) => {
                    e.stopPropagation();
                    requestPermissions();
                  }}
                  onMouseEnter={requestPermissions}
                  onFocus={requestPermissions}
                  // 40px, 48px on touch screens (gloves); see context/ui-requirements.md
                  sx={{
                    width: 40,
                    height: 40,
                    [COARSE_POINTER]: { width: 48, height: 48 },
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 1,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <MoreVert />
                </IconButton>
              </Dropdown>
            }
            title={
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: { xs: "0.85rem", sm: "0.9rem", md: "1rem" },
                }}
                noWrap
              >
                {instrument.tagNumber}
              </Typography>
            }
            sx={{
              pt: 0.75,
              pb: 0,
              px: 2.5,
              "& .MuiCardHeader-content": {
                overflow: "hidden",
                textAlign: "center",
              },
            }}
          />

          {/* Card Content */}
          <CardContent sx={{ pt: 1.5, px: 2.5, pb: 2 }}>
            {/* Instrument Type chip — from nested serializer field */}
            {instrumentTypeName && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <Chip
                  label={instrumentTypeName}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: "0.65rem", height: 20 }}
                />
              </Box>
            )}

            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "text.secondary",
                mb: 1,
                textAlign: "center",
              }}
            >
              {instrument.description}
            </Typography>

            <Divider sx={{ my: 0.75, opacity: 0.6 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
              {shouldShowLogisticsDetails && (
                <FieldRow label="Parent" value={parentComponentTag} />
              )}
              <FieldRow label="IO Panel" value={ioPanelName} />
              <FieldRow label="Channel" value={instrument.channel} />
              <FieldRow label="Card" value={instrument.card} />
              <FieldRow
                label="Communication Protocol"
                value={commProtocolName}
              />
              <FieldRow label="P&ID No." value={instrument.pidNumber} />
            </Box>
            <LogisticsNextAction item={instrument} sx={{ mt: 1 }} />
          </CardContent>
        </Card>
      </Grid>
    );
  }

  // List view
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        borderLeft: `3px solid ${ribbonColor}`,
        minHeight: 54,
      }}
    >
      <Card
        data-active={selectedInstrument?.id === instrument.id ? "" : undefined}
        onClick={handlePrimaryCardClick}
        sx={{
          width: "100%",
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          borderRadius: 2,
          transition: "all 0.18s ease",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          },
          ...sharedCardSx,
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 1.5,
            py: "8px !important",
            minHeight: 54,
            overflow: "hidden",
          }}
        >
          {/* DATA COLUMNS: clipped (not the actions) when the row is narrower than its columns */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
          {/* TRACKER */}
          <Box sx={{ width: 170, flexShrink: 0, minWidth: 0 }}>
            <AssetCardProgressTracker item={instrument} density="compact" />
            {shouldShowLogisticsDetails && parentComponentTag && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontWeight: 600,
                  color: "text.secondary",
                  mt: 0.5,
                }}
                noWrap
              >
                Parent: {parentComponentTag}
              </Typography>
            )}
            <LogisticsNextAction
              item={instrument}
              compact
              sx={{ mt: 0.5 }}
            />
          </Box>

          {/* TAG */}
          <Box sx={{ width: 115, flexShrink: 0, minWidth: 0 }}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: 700,
                color: "primary.main",
                fontSize: "0.82rem",
              }}
            >
              {instrument.tagNumber}
            </Typography>
          </Box>

          {/* IO PANEL */}
          <Box
            sx={{
              width: 120,
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <Typography variant="body2" noWrap>
              {ioPanelName || "—"}
            </Typography>
          </Box>

          {/* CHANNEL */}
          <Box
            sx={{
              width: 55,
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <Typography variant="body2" noWrap>
              {instrument.channel || "—"}
            </Typography>
          </Box>

          {/* CARD */}
          <Box
            sx={{
              width: 150,
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <Typography variant="body2" noWrap>
              {instrument.card || "—"}
            </Typography>
          </Box>

          {/* TYPE */}
          <Box
            sx={{
              width: 150,
              flexShrink: 0,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {instrumentTypeName ? (
              <Chip
                label={instrumentTypeName}
                size="small"
                color="primary"
                variant="outlined"
                sx={{
                  height: 20,
                  maxWidth: "100%",
                  fontSize: "0.68rem",
                  "& .MuiChip-label": {
                    px: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Box>

          {/* PID */}
          <Box sx={{ width: 110, flexShrink: 0, minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {pidTagNumber || "—"}
            </Typography>
          </Box>

          {/* COMM PROTOCOL */}
          <Box
            sx={{
              width: 130,
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{
                color: "text.secondary",
              }}
            >
              {commProtocolName || "—"}
            </Typography>
          </Box>

          {/* DESCRIPTION */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: "text.secondary",
                fontSize: "0.8rem",
              }}
            >
              {instrument.description || "—"}
            </Typography>
          </Box>
          </Box>

          {/* ACTIONS: pinned so they are never clipped when the data columns run out of room */}
          <Box
            sx={{
              flexShrink: 0,
              width: INSTRUMENT_LIST_ACTIONS_WIDTH,
              [COARSE_POINTER]: { width: INSTRUMENT_LIST_ACTIONS_WIDTH_TOUCH },
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.5,
            }}
          >
            {canEdit && (
              <ButtonBase
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditInstrument(instrument.id);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  minHeight: 40,
                  px: 1,
                  borderRadius: 1,
                  color: "success.main",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  // The page wrapper sets a 120px line-height that buttons inherit.
                  lineHeight: 1.5,
                  whiteSpace: "nowrap",
                  [COARSE_POINTER]: { minHeight: 48, px: 1.5 },
                }}
              >
                <Edit sx={{ fontSize: 18 }} />
                EDIT
              </ButtonBase>
            )}

            <Divider orientation="vertical" flexItem sx={{ mx: 0.25, flexShrink: 0 }} />

            <Dropdown
              menu={{ items: menuItems }}
              trigger={["click"]}
              open={dropdownOpen}
              onOpenChange={handleDropdownOpenChange}
            >
              <IconButton
                aria-label="More options"
                onClick={(e) => {
                  e.stopPropagation();
                  requestPermissions();
                }}
                onMouseEnter={requestPermissions}
                onFocus={requestPermissions}
                sx={{
                  width: 40,
                  height: 40,
                  [COARSE_POINTER]: { width: 48, height: 48 },
                }}
              >
                <MoreVert />
              </IconButton>
            </Dropdown>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InstrumentCard;
